'use client';

import type { ReactNode } from 'react';
import { Badge, Card, CardContent, CardHeader, Icon, type BadgeProps } from '../../components';
import { useTranslation } from '../../providers/UIProvider';
import { Temporal, formatShortDate } from '@insio/dates';
import { Decimal } from 'decimal.js';
import { cn } from '../../utils';
import { formatAmount } from '@insio/decimal';
import type { CurrencyCode } from '@insio/shared-types';
import { ReceiptDivider } from './ReceiptDivider';

export interface ReceiptLineItem {
  /** Optional quantity - when omitted, the item is displayed without a quantity prefix */
  quantity?: Decimal;
  /** Product or service name (or locKey for translation) */
  name: string;
  /** Price per item (unit price in Kč/ks) */
  pricePerUnit?: Decimal;
  /** VAT rate percentage (e.g., 21 for 21% DPH) */
  vatRate?: Decimal;
  /** Total price for this line item */
  price: Decimal;
  /** Drawn full width under the line, e.g. its purchase flow and pairing action. */
  footer?: ReactNode;
}

/** Any colour the status badge takes. */
export type ReceiptStatusVariant = NonNullable<BadgeProps['color']>;

export interface ReceiptPartner {
  /** Partner name */
  name: string;
  /** Partner address */
  address?: string;
  /** Company ID (IČO) */
  companyId?: string;
  /** Tax ID (DIČ) */
  taxId?: string;
}

export interface ReceiptProps {
  /** Merchant/company name */
  merchantName: string;
  /** Shop address */
  merchantAddress?: string;
  /** Company ID (IČO) */
  merchantId?: string;
  /** Tax ID (DIČ) */
  merchantTaxId?: string;
  /** Supplier information */
  supplier?: ReceiptPartner;
  /** Customer information */
  customer?: ReceiptPartner;
  /** Receipt date */
  date: Temporal.PlainDate;
  /** Line items (products/services) */
  lineItems: ReceiptLineItem[];
  /** Total amount without VAT */
  totalWithoutVat?: Decimal;
  /** VAT amount */
  vatAmount?: Decimal;
  /** VAT rate percentage (e.g., 21 for 21% DPH) */
  vatRate?: Decimal;
  /** Show VAT column/rows. False for non-tax documents. @default true */
  showVat?: boolean;
  /** Total amount */
  total: Decimal;
  /** Currency display label rendered next to amounts (e.g. "Kč", "EUR"). */
  currency: string;
  /** ISO 4217 currency code used to pick decimal precision (e.g. "CZK", "EUR", "JPY"). Defaults to 2 decimal places when omitted. */
  currencyCode?: CurrencyCode;
  /** Payment method - display text. When omitted, the payment-method row is hidden. */
  paymentMethod?: string | null;
  /** Status text - display text (use translated string from parent) */
  status: string;
  /** Badge variant for status */
  statusVariant?: ReceiptStatusVariant;
  /** Transaction or receipt ID */
  transactionId?: string;
  /** Footer message - overrides translated default */
  footerMessage?: string;
  /** BCP 47 locale for number/date formatting. Defaults to the value from FormatProvider (account preference). */
  locale?: string;
  /** Custom amount formatter - when provided, overrides locale-based formatting */
  formatAmountCustom?: (amount: Decimal) => string;
  /** Additional class name */
  className?: string;

  // Editable slots: when provided, a slot replaces its static value so the host
  // can render an inline editor in its place.
  /** Replaces the merchant name. */
  merchantNameSlot?: ReactNode;
  /** Centered block under the merchant name; replaces the static IČO/DIČ line. */
  merchantDetailsSlot?: ReactNode;
  /** Replaces the receipt date value. */
  dateSlot?: ReactNode;
  /** Replaces the entire line-items region (e.g. a modal-opening editor). */
  lineItemsSlot?: ReactNode;
  /** Replaces the entire VAT-breakdown region. */
  vatRecapSlot?: ReactNode;
  /** Replaces the total amount value. */
  totalSlot?: ReactNode;
  /** Payment-method row (label + value/editor), rendered under the total. */
  paymentMethodSlot?: ReactNode;
  /** Banking-connection section, rendered under the payment method. */
  bankingDetailsSlot?: ReactNode;
  /** Accounting-currency conversion block, rendered after the banking section. */
  accountingConversionSlot?: ReactNode;
}

/**
 * Receipt - A printable receipt component displaying transaction details
 *
 * Displays merchant info, date/time, line items, total, payment method,
 * status badge, and footer. Supports i18n via useTranslation and
 * configurable locale/formatAmount for currencies.
 */
export function Receipt({
  merchantName,
  merchantId,
  merchantTaxId,
  supplier,
  customer,
  date,
  lineItems,
  totalWithoutVat,
  vatAmount,
  vatRate,
  showVat = true,
  total,
  currency,
  currencyCode,
  paymentMethod,
  status,
  statusVariant = 'warning',
  transactionId,
  footerMessage,
  locale,
  formatAmountCustom,
  className,
  merchantNameSlot,
  merchantDetailsSlot,
  dateSlot,
  lineItemsSlot,
  vatRecapSlot,
  totalSlot,
  paymentMethodSlot,
  bankingDetailsSlot,
  accountingConversionSlot
}: ReceiptProps) {
  const t = useTranslation('modules.receipt');
  // Explicit prop takes priority over context.
  const effectiveLocale = locale;
  const effectiveDateLocale = locale;

  const formatPrice = (amount: Decimal): string =>
    formatAmountCustom
      ? formatAmountCustom(amount)
      : formatAmount(amount, { currencyCode, locale: effectiveLocale });

  const displayFooterMessage = footerMessage ?? t('footerMessage');

  // Drop the VAT column for non-tax documents (4 columns instead of 5).
  const lineItemsGridClass = showVat
    ? 'grid-cols-[minmax(40%,1fr)_minmax(60px,auto)_minmax(80px,auto)_minmax(60px,auto)_minmax(90px,auto)]'
    : 'grid-cols-[minmax(40%,1fr)_minmax(60px,auto)_minmax(80px,auto)_minmax(90px,auto)]';

  return (
    <div className={cn('relative w-full max-w-2xl mx-auto py-sm', className)}>
      {/* Perforated edge - top of receipt */}
      <div
        aria-hidden
        className='absolute left-0 right-0 pointer-events-none top-0 z-10'
        style={{
          height: '8px',
          backgroundImage: `radial-gradient(circle at 50% 0, transparent 6px, #ffffff 6px)`,
          backgroundSize: '16px 16px',
          backgroundPosition: '0 0'
        }}
      />
      {/* Perforated edge - bottom of receipt */}
      <div
        aria-hidden
        className='absolute left-0 right-0 pointer-events-none bottom-0 z-10'
        style={{
          height: '8px',
          backgroundImage: `radial-gradient(circle at 50% 0, transparent 6px, #ffffff 6px)`,
          backgroundSize: '16px 16px',
          backgroundPosition: '0 0',
          transform: 'rotate(180deg)'
        }}
      />
      <Card
        data-slot='receipt'
        className='shadow-md rounded-none border-t-0 border-b-0 border-none font-mono'
      >
        <CardHeader className='flex flex-col items-center text-center gap-sm pb-md px-xl'>
          <h2 className='text-lg font-semibold text-foreground-base'>
            {merchantNameSlot ?? merchantName}
          </h2>
          {merchantDetailsSlot ??
            ((merchantId || merchantTaxId) && (
              <p className='text-sm text-foreground-muted'>
                {merchantId && `${t('merchantId')}: ${merchantId}`}
                {merchantId && merchantTaxId && '\u00A0\u00A0\u00A0'}
                {merchantTaxId && `${t('merchantTaxId')}: ${merchantTaxId}`}
              </p>
            ))}
        </CardHeader>

        <div className='px-xl'>
          <ReceiptDivider />
        </div>

        {/* Supplier and Customer columns */}
        {(supplier || customer) && (
          <>
            <div className='px-xl pt-md'>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-md text-sm'>
                {/* Supplier column */}
                <div className='flex flex-col gap-xs'>
                  <span className='text-xs uppercase text-foreground-muted tracking-wide'>
                    {t('supplier')}
                  </span>
                  {supplier && (
                    <>
                      <span className='font-semibold text-foreground-base'>{supplier.name}</span>
                      {supplier.address && (
                        <span className='text-foreground-muted text-xs'>{supplier.address}</span>
                      )}
                      {supplier.companyId && (
                        <span className='text-foreground-muted text-xs'>
                          {t('companyId')}: {supplier.companyId}
                        </span>
                      )}
                      {supplier.taxId && (
                        <span className='text-foreground-muted text-xs'>
                          {t('taxId')}: {supplier.taxId}
                        </span>
                      )}
                    </>
                  )}
                </div>

                {/* Customer column */}
                <div className='flex flex-col gap-xs md:items-end md:text-right'>
                  <span className='text-xs uppercase text-foreground-muted tracking-wide'>
                    {t('customer')}
                  </span>
                  {customer && (
                    <>
                      <span className='font-semibold text-foreground-base'>{customer.name}</span>
                      {customer.address && (
                        <span className='text-foreground-muted text-xs'>{customer.address}</span>
                      )}
                      {customer.companyId && (
                        <span className='text-foreground-muted text-xs'>
                          {t('companyId')}: {customer.companyId}
                        </span>
                      )}
                      {customer.taxId && (
                        <span className='text-foreground-muted text-xs'>
                          {t('taxId')}: {customer.taxId}
                        </span>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className='px-xl pt-md'>
              <ReceiptDivider />
            </div>
          </>
        )}

        <CardContent className='flex flex-col gap-md pt-sm px-xl pb-lg'>
          {/* Date */}
          <div className='flex flex-row justify-center items-center'>
            <span className='flex items-center justify-center gap-sm text-sm text-foreground-muted w-full sm:w-50'>
              <Icon name='calendar' size='sm' className='text-foreground-muted' />
              {dateSlot ?? formatShortDate(date, effectiveDateLocale)}
            </span>
          </div>

          <ReceiptDivider />

          {/* Line items */}
          {lineItemsSlot ? (
            <div className='flex flex-col gap-sm'>{lineItemsSlot}</div>
          ) : (
            <div className='flex flex-col gap-sm'>
              {/* Table header - Desktop (5 columns, or 4 without VAT) */}
              <div
                className={cn(
                  'max-md:hidden! grid gap-md text-xs uppercase text-foreground-muted tracking-wide',
                  lineItemsGridClass
                )}
              >
                <span>{t('itemName')}</span>
                <span className='text-center'>{t('quantity')}</span>
                <span className='text-right'>{t('pricePerUnit')}</span>
                {showVat && <span className='text-right'>{t('vat')}</span>}
                <span className='text-right'>{t('totalPrice')}</span>
              </div>

              {/* Table header - Mobile (2 columns) */}
              <div className='md:hidden! grid grid-cols-[1fr_auto] gap-md text-xs uppercase text-foreground-muted tracking-wide'>
                <span>{t('itemName')}</span>
                <span className='text-right'>{t('totalPrice')}</span>
              </div>

              <ReceiptDivider />

              {/* Table rows - Desktop */}
              <div className='max-md:hidden! flex flex-col gap-md'>
                {lineItems.map((item, index) => (
                  <div key={index} className='flex flex-col gap-xs'>
                    <div className={cn('grid gap-md items-baseline', lineItemsGridClass)}>
                      <span className='text-sm text-foreground-base'>{item.name}</span>
                      <span className='text-sm text-foreground-muted text-center'>
                        {item.quantity != null ? item.quantity.toString() : '-'}
                      </span>
                      <span className='text-sm text-foreground-muted text-right'>
                        {item.pricePerUnit != null ? formatPrice(item.pricePerUnit) : '-'}
                      </span>
                      {showVat && (
                        <span className='text-sm text-foreground-muted text-right'>
                          {item.vatRate != null ? `${item.vatRate.toString()}%` : '-'}
                        </span>
                      )}
                      <span className='text-sm font-semibold text-foreground-base text-right'>
                        {formatPrice(item.price)}
                      </span>
                    </div>
                    {item.footer && <div className='min-w-0'>{item.footer}</div>}
                  </div>
                ))}
              </div>

              {/* Table rows - Mobile (2 columns: name + total, details underneath) */}
              <div className='md:hidden! flex flex-col gap-md'>
                {lineItems.map((item, index) => (
                  <div key={index} className='flex flex-col gap-xs'>
                    <div className='grid grid-cols-[1fr_auto] gap-md items-start'>
                      <div className='flex flex-col gap-xs'>
                        <span className='text-sm text-foreground-base'>{item.name}</span>
                        {/* Details underneath: "5x 109,00Kč · DPH 21 %" */}
                        <span className='text-xs text-foreground-muted'>
                          {item.quantity != null && `${item.quantity.toString()}x `}
                          {item.pricePerUnit != null && `${formatPrice(item.pricePerUnit)}`}
                          {showVat &&
                            item.vatRate != null &&
                            ` · ${t('vat')} ${item.vatRate.toString()} %`}
                        </span>
                      </div>
                      <span className='text-sm font-semibold text-foreground-base text-right'>
                        {formatPrice(item.price)}
                      </span>
                    </div>
                    {item.footer && <div className='min-w-0'>{item.footer}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          <ReceiptDivider />

          {/* VAT breakdown (if provided) */}
          {vatRecapSlot ? (
            <>
              {vatRecapSlot}
              <ReceiptDivider />
            </>
          ) : (
            (totalWithoutVat != null || vatAmount != null) && (
              <>
                <div className='flex flex-col gap-sm'>
                  {totalWithoutVat != null && (
                    <div className='flex flex-row justify-between items-baseline'>
                      <span className='text-sm text-foreground-muted'>{t('totalWithoutVat')}</span>
                      <span className='text-sm text-foreground-muted'>
                        {formatPrice(totalWithoutVat)} {currency}
                      </span>
                    </div>
                  )}
                  {vatAmount != null && (
                    <div className='flex flex-row justify-between items-baseline'>
                      <span className='text-sm text-foreground-muted'>
                        {t('vatAmount')} {vatRate != null ? `${vatRate.toString()} %` : ''}
                      </span>
                      <span className='text-sm text-foreground-muted'>
                        {formatPrice(vatAmount)} {currency}
                      </span>
                    </div>
                  )}
                </div>

                <ReceiptDivider />
              </>
            )
          )}

          {/* Total — full-width editor when slotted; label + value row otherwise */}
          {totalSlot ?? (
            <div className='flex flex-row justify-between items-baseline'>
              <span className='text-lg font-semibold text-foreground-base'>{t('total')}</span>
              <span className='text-lg font-bold text-foreground-base'>
                {formatPrice(total)} {currency}
              </span>
            </div>
          )}

          {(paymentMethodSlot ||
            paymentMethod != null ||
            bankingDetailsSlot ||
            accountingConversionSlot) && <ReceiptDivider />}

          {/* Payment method */}
          {paymentMethodSlot
            ? paymentMethodSlot
            : paymentMethod != null && (
                <div className='flex flex-row justify-between items-center'>
                  <span className='text-sm text-foreground-muted'>{t('payment')}:</span>
                  <span className='text-sm text-foreground-base'>{paymentMethod}</span>
                </div>
              )}

          {/* Banking connection (bank transfer only) */}
          {bankingDetailsSlot && (
            <>
              <ReceiptDivider />
              {bankingDetailsSlot}
            </>
          )}

          {/* Accounting-currency conversion */}
          {accountingConversionSlot && (
            <>
              <ReceiptDivider />
              {accountingConversionSlot}
            </>
          )}

          <ReceiptDivider />

          {/* Status */}
          <div className='flex flex-row justify-between items-center gap-sm'>
            <span className='text-sm text-foreground-muted'>{t('status')}:</span>
            <Badge color={statusVariant} size='sm'>
              {status}
            </Badge>
          </div>

          <ReceiptDivider />

          {/* Footer */}
          <div className='flex flex-col items-center text-center gap-2xs pt-sm'>
            <p className='text-sm text-foreground-muted'>{displayFooterMessage}</p>
            {transactionId && <p className='text-xs text-foreground-muted'>{transactionId}</p>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
